import { useContext, useEffect, useRef, useSyncExternalStore } from 'react';
import { getAdminClient } from 'lane-shared/apollo';
import {
  BulkTeamManagementContext,
  ActiveWizard,
} from '../context/BulkTeamManagementContext';
import { useFilteredPermissions } from '../../../admin/channel/hooks/useFilteredPermissions';
import { PermissionsAssignmentWizardEngine } from './PermissionsAssignmentWizardEngine';
import { useTeamsSelectionGroupRoles } from './useTeamsSelectionGroupRoles';

export function useAssignPermissionsWizard() {
  const { selectedChannelIds, activeWizard, setActiveWizard } = useContext(
    BulkTeamManagementContext
  );
  const client = getAdminClient();
  const isOpen = activeWizard === ActiveWizard.PermissionsAssignment;

  const engineRef = useRef<PermissionsAssignmentWizardEngine | null>(null);

  if (
    !engineRef.current ||
    engineRef.current.getChannelIds() !== selectedChannelIds
  ) {
    engineRef.current = new PermissionsAssignmentWizardEngine(
      client,
      selectedChannelIds
    );
  }

  const engine = engineRef.current;

  const snapshot = useSyncExternalStore(
    engine.subscribe.bind(engine),
    engine.getSnapshot.bind(engine)
  );

  const teamsSelection = useTeamsSelectionGroupRoles({
    channelIds: selectedChannelIds,
    isOpen,
    onGroupRoleIdsChange: engine.selectGroupRoleIds.bind(engine),
  });

  const permissionCategories = useFilteredPermissions();

  const close = () => {
    engine.reset();
    teamsSelection.reset();
    setActiveWizard(null);
  };

  const closeRef = useRef(close);

  closeRef.current = close;

  useEffect(() => {
    if (snapshot.submitOutcome) {
      closeRef.current();
    }
  }, [snapshot.submitOutcome]);

  return {
    isOpen,
    step: snapshot.step,
    teamRows: teamsSelection.rows,
    teamsTotal: teamsSelection.total,
    teamsLoading: teamsSelection.loading,
    teamsError: teamsSelection.error,
    teamsKeyword: teamsSelection.keyword,
    teamsPage: teamsSelection.page,
    teamsPageSize: teamsSelection.pageSize,
    selectedTeamNames: teamsSelection.selectedTeamNames,
    onTeamsKeywordChange: teamsSelection.handleKeywordChange,
    onTeamsPageChange: teamsSelection.handlePageChange,
    onTeamsPageSizeChange: teamsSelection.handlePageSizeChange,
    selectTeamNames: teamsSelection.selectTeamNames,
    permissionCategories,
    selectedPermissions: snapshot.selectedPermissions,
    canAdvance: snapshot.canAdvance,
    isSubmitting: snapshot.isSubmitting,
    submitError: snapshot.submitError,
    submitOutcome: snapshot.submitOutcome,
    channelCount: selectedChannelIds.length,
    teamCount: teamsSelection.selectedTeamNames.length,
    selectPermissions: engine.selectPermissions.bind(engine),
    next: engine.next.bind(engine),
    back: engine.back.bind(engine),
    confirm: engine.confirm.bind(engine),
    close,
  };
}
